import { useId } from 'react';
import {
    Area,
    AreaChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { enumerateDates } from '~utils/enumerate-dates';
import generatePalette from '~utils/generate-palette';
import {
    MULTILINE_CHART_COLORS,
    UNKNOWN_CT_BLUE_OPACITY_15,
} from '~styles/colors';
import { useAnimateAfterDataChange } from '~styles/use-animate-after-data-change';
import { MOTION_DURATION_MS, MOTION_EASE_RECHARTS } from '~styles/motion';
import { useTweenedValue } from '~containers/analytics/components/kpi-tile/use-tweened-value';
import { formatDownloadsAxisDate } from './downloads-form.helpers';
import { formatPercentValue } from './split-form.helpers';
import { resolveSeriesColors } from './palette-colors';
import { AREA_STYLE_PROPS, AreaGradientDef, CHART_MARGIN } from './area-chart';
import style from './multiline-chart.module.scss';
import type { KpiMultilineUnit, KpiPalette } from '../types';

/** One x-axis day, plus one cell per series keyed by its label. */
export interface MultilineChartRow {
    date: string;
    [seriesLabel: string]: string | number | null;
}

export interface MultilineChartSeries {
    label: string;
    /** Brand override; omitted falls back to the positional ramp. */
    color?: string;
}

export interface MultilineLegendSeries extends MultilineChartSeries {
    value: number | null;
}

/**
 * Spread N named series onto one row per day of `range`. Generic counterpart
 * of `buildDownloadsChartRows`: downloads keeps its own copy so its
 * store-lag-specific refusal reasoning stays put (see that file).
 */
export const buildMultilineChartRows = (
    range: { start: string; end: string },
    series: {
        label: string;
        points: { date: string; value: number | null }[];
    }[]
): MultilineChartRow[] => {
    const days = enumerateDates(range.start, range.end);
    // A capped enumeration means `range` is longer than this can draw whole;
    // refuse rather than silently narrow the axis.
    if (days.length > 0 && days[days.length - 1] !== range.end) {
        return [];
    }
    const byLabel = series.map((entry) => ({
        label: entry.label,
        values: new Map(entry.points.map((point) => [point.date, point.value])),
    }));

    return days.map((date) => {
        const row: MultilineChartRow = { date };
        for (const entry of byLabel) {
            row[entry.label] = entry.values.get(date) ?? null;
        }
        return row;
    });
};

/** Colour per series: the palette's line ramp (`resolveSeriesColors`),
 * otherwise the default multiline ramp extended by `generatePalette`; an
 * explicit `entry.color` always wins. */
export const resolveColors = (
    series: MultilineChartSeries[],
    palette?: KpiPalette
): string[] => {
    const ramp =
        resolveSeriesColors(
            series.map((entry) => entry.label),
            palette,
            'line'
        ) ?? generatePalette(MULTILINE_CHART_COLORS, series.length);
    return series.map((entry, index) => entry.color ?? ramp[index]);
};

/** A count-unit y-axis tick in compact notation (`200000` -> `'200k'`), for
 * the `area` chart style; `Intl`'s own compact suffixes are uppercase. */
export const formatCompactCount = (value: number): string =>
    new Intl.NumberFormat('en-US', { notation: 'compact' })
        .format(value)
        .toLowerCase();

const formatSeriesValue = (
    value: number | null,
    unit: KpiMultilineUnit
): string => {
    if (value == null) return 'N/A';
    return unit === 'percent'
        ? formatPercentValue(value)
        : value.toLocaleString('en-US');
};

/** Anything recharts hands us besides a plain number (including a missing
 * point) reads as `formatSeriesValue`'s own N/A. */
export const formatTooltipValue = (
    value: number | string | readonly (number | string)[] | undefined,
    unit: KpiMultilineUnit
): string => formatSeriesValue(typeof value === 'number' ? value : null, unit);

export interface MultilineChartProps {
    rows: MultilineChartRow[];
    series: MultilineChartSeries[];
    unit: KpiMultilineUnit;
    /** `'area'`: draws each series as a gradient-filled area instead of a
     * plain line. Omitted = today's line chart. */
    chartStyle?: 'area';
    /** Colour ramp; see `KpiCardSpecBase.palette`. */
    palette?: KpiPalette;
}

/**
 * Shared multi-line renderer: N named lines (or, with `chartStyle: 'area'`,
 * gradient-filled areas) over rows already spread onto an explicit axis
 * (`buildMultilineChartRows`), one colour per series from a shared positional
 * ramp, and a y-axis formatted as counts or percents. Row-building and colour
 * choices that carry source-specific meaning (downloads' store-lag clipping
 * and brand colours) stay with their own form; this draws whatever it is
 * handed.
 */
export const MultilineChart = ({
    rows,
    series,
    unit,
    chartStyle,
    palette,
}: MultilineChartProps) => {
    const animate = useAnimateAfterDataChange(
        rows.map((row) => Object.values(row).join(',')).join('|')
    );
    const colors = resolveColors(series, palette);
    // Suffixes every gradient id so two area cards on one page never collide.
    // useId()'s colons are valid in an id but not in a CSS selector.
    const gradientId = `multiline-area-${useId().replace(/:/g, '')}`;

    const axes = (
        <>
            <CartesianGrid
                vertical={false}
                strokeDasharray="4 4"
                stroke={UNKNOWN_CT_BLUE_OPACITY_15}
            />
            <XAxis
                dataKey="date"
                tick={{ fontSize: 10 }}
                tickMargin={4}
                tickFormatter={formatDownloadsAxisDate}
                minTickGap={16}
            />
            <YAxis
                tick={{ fontSize: 10 }}
                // The axis band is all the room a tick label gets: a
                // wider one overflows the chart's left edge and is
                // clipped (28000 reading as 8000).
                width="auto"
                allowDecimals={unit === 'percent'}
                tickFormatter={
                    unit === 'percent'
                        ? formatPercentValue
                        : chartStyle === 'area'
                        ? formatCompactCount
                        : undefined
                }
            />
            <Tooltip formatter={(value) => formatTooltipValue(value, unit)} />
        </>
    );

    return (
        <div className={style.multiline_chart}>
            <ResponsiveContainer width="100%" height={200}>
                {chartStyle === 'area' ? (
                    <AreaChart data={rows} margin={CHART_MARGIN}>
                        <defs>
                            {series.map((entry, index) => (
                                <AreaGradientDef
                                    key={entry.label}
                                    id={`${gradientId}-${index}`}
                                    color={colors[index]}
                                />
                            ))}
                        </defs>
                        {axes}
                        {series.map((entry, index) => (
                            <Area
                                key={entry.label}
                                {...AREA_STYLE_PROPS}
                                dataKey={entry.label}
                                stroke={colors[index]}
                                fill={`url(#${gradientId}-${index})`}
                                isAnimationActive={animate}
                                animationDuration={MOTION_DURATION_MS}
                                animationEasing={MOTION_EASE_RECHARTS}
                                animationBegin={0}
                            />
                        ))}
                    </AreaChart>
                ) : (
                    <LineChart data={rows} margin={CHART_MARGIN}>
                        {axes}
                        {series.map((entry, index) => (
                            <Line
                                key={entry.label}
                                dataKey={entry.label}
                                stroke={colors[index]}
                                strokeWidth={2}
                                connectNulls={false}
                                dot={{
                                    r: 2,
                                    strokeWidth: 0,
                                    fill: colors[index],
                                }}
                                activeDot={{ r: 4 }}
                                isAnimationActive={animate}
                                animationDuration={MOTION_DURATION_MS}
                                animationEasing={MOTION_EASE_RECHARTS}
                                animationBegin={0}
                            />
                        ))}
                    </LineChart>
                )}
            </ResponsiveContainer>
        </div>
    );
};

export interface MultilineLegendProps {
    series: MultilineLegendSeries[];
    unit: KpiMultilineUnit;
    /** Colour ramp, matching the chart's own; see
     * `MultilineChartProps.palette`. Omitted = the default multiline ramp. */
    palette?: KpiPalette;
}

/** A numeric figure tweens toward its new value on a data change; a missing
 * or non-numeric one renders `N/A` as it always has, untweened. */
const MultilineLegendItem = ({
    entry,
    unit,
    color,
}: {
    entry: MultilineLegendSeries;
    unit: KpiMultilineUnit;
    color: string;
}) => {
    const numericValue =
        typeof entry.value === 'number' && Number.isFinite(entry.value)
            ? entry.value
            : null;
    const tweenedValue = useTweenedValue(numericValue ?? 0);

    return (
        <li className={style.multiline_legend_item}>
            <span
                className={style.multiline_legend_dot}
                style={{ background: color }}
            />
            <span className={style.multiline_legend_label}>
                {entry.label} -{' '}
                <strong>
                    {numericValue === null
                        ? 'N/A'
                        : unit === 'percent'
                        ? formatPercentValue(tweenedValue)
                        : Math.round(tweenedValue).toLocaleString('en-US')}
                </strong>
            </span>
        </li>
    );
};

/** Generic legend: one dot, label and value per series. Downloads keeps its
 * own legend instead (total, freshness and missing notes have no analogue
 * on a generic series). */
export const MultilineLegend = ({
    series,
    unit,
    palette,
}: MultilineLegendProps) => {
    const colors = resolveColors(series, palette);

    return (
        <ul className={style.multiline_legend}>
            {series.map((entry, index) => (
                <MultilineLegendItem
                    key={entry.label}
                    entry={entry}
                    unit={unit}
                    color={colors[index]}
                />
            ))}
        </ul>
    );
};
